import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Stack,
} from "react-bootstrap";
import { HttpData, HttpNoData } from "../../../../Core";
import { MdEdit } from "react-icons/md";
import RangeSizeEdit from "./RangeSizeEdit";
import { HiPlus } from "react-icons/hi";
import RangeThicknessEdit from "./RangeThicknessEdit";
import RangePriceOnCount from "./RangePriceOnCount";
import ProductMappingsModal from "./ProductMappingsModal";
import Select from "react-select";

export default function ProductModalEdit({
  jwt,
  product,
  show,
  categories,
  subtypes,
  materials,
  onHide,
  onDeleted,
  onUpdated,
}) {
  const [isSimpleProduct, setIsSimpleProduct] = useState(
    product.maxSize == -1 &&
    product.minSize == -1 &&
    product.standardThickness == -1 &&
    product.standardSize == -1,
  );
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description);
  const [categoryId, setCategoryId] = useState(product.categoryId);
  const [subtypeId, setSubtypeId] = useState(product.subtypeId || "");
  const [materialId, setMaterialId] = useState(product.materialId || "");
  const [maxSize, setMaxSize] = useState(product.maxSize);
  const [minSize, setMinSize] = useState(product.minSize);
  const [standardThickness, setStandardThickness] = useState(
    product.standardThickness,
  );
  const [standardWeight, setStandardWeight] = useState(product.standardWeight);
  const [standardSize, setStandardSize] = useState(product.standardSize);
  const [standardPrice, setStandardPrice] = useState("");

  const [useExactWeights, setUseExactWeights] = useState(product.useExactWeights || false);

  const [linkedAdditions, setLinkedAdditions] = useState([]);
  const [allAdditions, setAllAdditions] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [thickness, setThickness] = useState([]);
  const [priceOnCounts, setPriceOnCounts] = useState([]);
  const [productCoatings, setProductCoatings] = useState([]);

  const [isEditSizes, setEditSizes] = useState(false);
  const [isEditThickness, setIsEditThickness] = useState(false);
  const [isEditPriceOnCount, setIsEditPriceOnCount] = useState(false);
  const [isEditCoatings, setIsEditCoatings] = useState(false);

  const [isEditMappings, setIsEditMappings] = useState(false);

  useEffect(() => {
    async function didMount() {
      const sizesResponse = await HttpNoData(
        `/api/v1/calculateOrderCost/productRangeSizes/${product.id}`,
        "GET",
        jwt,
      );
      setSizes(sizesResponse.data ?? []);
      setStandardPrice(sizesResponse.data[0]?.price || "");

      const [linkedRes, allRes] = await Promise.all([
        HttpNoData(`/api/v1/calculateOrderCost/products/additions/${product.id}`, "GET", jwt),
        HttpNoData(`/api/v1/calculateOrderCost/products/additions`, "GET", jwt),
      ]);
      setLinkedAdditions(linkedRes.data ?? []);
      setAllAdditions(allRes.data ?? []);

      const thicknessResponse = await HttpNoData(
        `/api/v1/calculateOrderCost/productRangeThickness/${product.id}`,
        "GET",
        jwt,
      );
      setThickness(thicknessResponse.data ?? []);

      const pricesOnCountResponse = await HttpNoData(
        `/api/v1/calculateOrderCost/products/countPrices/${product.id}`,
        "GET",
        jwt,
      );
      setPriceOnCounts(pricesOnCountResponse.data ?? []);

      const coatingsResponse = await HttpNoData(
        `/api/v1/calculateOrderCost/products/${product.id}/coatings`,
        "GET",
        jwt,
      );
      setProductCoatings(coatingsResponse.data ?? []);
    }

    didMount();
  }, [product.id, jwt]);
  const createSizeAsync = async (size, price, weight, productId) => {
    const sizesResponse = await HttpData(
      `/api/v1/calculateOrderCost/productRangeSizes`,
      "POST",
      jwt,
      {
        price: +price,
        size: +size,
        weight: weight ? +weight : null,
        productId: +productId,
      },
    );

    if (sizesResponse.statusSuccessful)
      setSizes([...sizes, sizesResponse.data]);
    else alert("Не получилось создать");
  };

  const handleAdditionsChange = async (selected) => {
    const selectedIds = selected.map(s => s.value);
    const currentIds = linkedAdditions.map(a => a.id);
    for (const id of selectedIds.filter(id => !currentIds.includes(id)))
      await HttpNoData(`/api/v1/calculateOrderCost/products/additions/${id}/link/${product.id}`, 'POST', jwt);
    for (const id of currentIds.filter(id => !selectedIds.includes(id)))
      await HttpNoData(`/api/v1/calculateOrderCost/products/additions/${id}/link/${product.id}`, 'DELETE', jwt);
    setLinkedAdditions(allAdditions.filter(a => selectedIds.includes(a.id)));
  };

  const createThicknessAsync = async (thicknessValue, percent, productId) => {
    const sizesResponse = await HttpData(
      `/api/v1/calculateOrderCost/productRangeThickness`,
      "POST",
      jwt,
      {
        thickness: +thicknessValue,
        percent: +percent,
        productId: +productId,
      },
    );

    if (sizesResponse.statusSuccessful)
      setThickness([...thickness, sizesResponse.data]);
    else alert("Не получилось создать");
  };

  const createPriceOnCountAsync = async (count, discount, productId) => {
    if (priceOnCounts.some((p) => +p.count === +count)) {
      alert(
        `Скидка для ${count} шт. уже есть в списке — отредактируйте её через карандаш вместо создания новой, иначе на этот порог будут действовать сразу две скидки.`,
      );
      return;
    }

    const countPricesResponse = await HttpData(
      `/api/v1/calculateOrderCost/products/countPrices`,
      "POST",
      jwt,
      {
        count: +count,
        discount: +discount,
        productId: +productId,
      },
    );

    if (countPricesResponse.statusSuccessful)
      setPriceOnCounts([...priceOnCounts, countPricesResponse.data]);
    else alert("Не получилось создать");
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (isSimpleProduct && sizes.length > 0) {
      const sizeResponse = await HttpData(
        `/api/v1/calculateOrderCost/productRangeSizes/${sizes[0].id}`,
        "PUT",
        jwt,
        {
          price: +standardPrice,
          size: -1,
          productId: +product.id,
        },
      );
    }

    const response = await HttpData(
      `/api/v1/calculateOrderCost/products/${product.id}`,
      "PUT",
      jwt,
      {
        name: name,
        description: description,
        categoryId: +categoryId,
        subtypeId: subtypeId ? +subtypeId : null,
        materialId: materialId ? +materialId : null,
        maxSize: +maxSize,
        minSize: +minSize,
        standardThickness: +standardThickness,
        standardWeight: +standardWeight,
        standardSize: +standardSize,
        priority: product.priority,
        useExactWeights: useExactWeights,
      },
    );

    if (response.statusSuccessful)
      onUpdated({
        ...product,
        name: name,
        description: description,
        categoryId: +categoryId,
        subtypeId: subtypeId ? +subtypeId : null,
        materialId: materialId ? +materialId : null,
        maxSize: +maxSize,
        minSize: +minSize,
        standardThickness: +standardThickness,
        standardWeight: +standardWeight,
        standardSize: +standardSize,
        useExactWeights: useExactWeights,
      });
    else alert(`Не получилось обновить: ${response.error}`);

    onHide();
  };

  const onDelete = async () => {
    const response = await HttpNoData(
      `/api/v1/calculateOrderCost/products/${product.id}`,
      "DELETE",
      jwt,
    );

    if (response.statusSuccessful) onDeleted();
    else alert(`Не получилось удалить: ${response.error}`);
  };

  if (isEditSizes) {
    return (
      <Modal show={isEditSizes} onHide={() => setEditSizes(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Размеры для {product.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Размеры</Form.Label>
            <ListGroup>
              {sizes.length > 0 ? (
                sizes.map((size) => (
                  <RangeSizeEdit
                    key={size.id}
                    jwt={jwt}
                    rangeSize={size}
                    onDeleted={() =>
                      setSizes([...sizes.filter((f) => f.id != size.id)])
                    }
                  />
                ))
              ) : (
                <Alert variant="warning">Пусто</Alert>
              )}
              <Button
                variant="success"
                onClick={() => {
                  const size = +prompt("Введите размер (в мм)", 100);
                  const price = +prompt("Введите стоимость", 1000);
                  const weight = +prompt("Введите вес (в граммах)", 100);
                  createSizeAsync(size, price, weight, product.id);
                }}
              >
                <HiPlus />
              </Button>
            </ListGroup>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditSizes(false)}>
            Назад
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  if (isEditThickness) {
    return (
      <Modal show={isEditThickness} onHide={() => setIsEditThickness(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Толщина для {product.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Толщина</Form.Label>
            <ListGroup>
              {thickness.length > 0 ? (
                thickness.map((item) => (
                  <RangeThicknessEdit
                    key={item.id}
                    jwt={jwt}
                    rangeThickness={item}
                    onDeleted={() =>
                      setThickness([
                        ...thickness.filter((f) => f.id != item.id),
                      ])
                    }
                  />
                ))
              ) : (
                <Alert variant="warning">Пусто</Alert>
              )}
              <Button
                variant="success"
                onClick={() => {
                  const thicknessVal = prompt("Введите размер (в мм)", 6);
                  const percent = +prompt("Введите ставку (в %)", 10);
                  createThicknessAsync(thicknessVal, percent, product.id);
                }}
              >
                <HiPlus />
              </Button>
            </ListGroup>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setIsEditThickness(false)}>
            Назад
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  if (isEditPriceOnCount) {
    return (
      <Modal
        show={isEditPriceOnCount}
        onHide={() => setIsEditPriceOnCount(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Скидки от кол-ва для {product.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Скидки от кол-ва</Form.Label>
            <ListGroup>
              {priceOnCounts.length > 0 ? (
                priceOnCounts.map((item) => (
                  <RangePriceOnCount
                    key={item.id}
                    jwt={jwt}
                    range={item}
                    onDeleted={() =>
                      setPriceOnCounts([
                        ...priceOnCounts.filter((f) => f.id != item.id),
                      ])
                    }
                  />
                ))
              ) : (
                <Alert variant="warning">Пусто</Alert>
              )}
              <Stack direction="horizontal">
                <Button
                  variant="success"
                  onClick={() => {
                    const count = +prompt("Введите кол-во", 100);
                    const discount = +prompt(
                      "Введите скидку в рублях на шт.",
                      10,
                    );
                    createPriceOnCountAsync(count, -discount, product.id);
                  }}
                >
                  B рублях
                </Button>
                <Button
                  variant="success"
                  onClick={() => {
                    const count = +prompt("Введите кол-во", 100);
                    const discount = +prompt(
                      "Введите скидку процентах на шт.",
                      10,
                    );
                    createPriceOnCountAsync(count, discount, product.id);
                  }}
                >
                  В процентах
                </Button>
              </Stack>
            </ListGroup>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setIsEditPriceOnCount(false)}
          >
            Назад
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  if (isEditCoatings) {
    return (
      <CoatingSelectionModal
        show={isEditCoatings}
        onHide={() => setIsEditCoatings(false)}
        jwt={jwt}
        productId={product.id}
        productCoatings={productCoatings}
        setProductCoatings={setProductCoatings}
      />
    );
  }
  if (isEditMappings) {
    return (
      <ProductMappingsModal
        show={isEditMappings}
        onHide={() => setIsEditMappings(false)}
        jwt={jwt}
        sourceProductId={product.id}
        sourceType="Standard"
      />
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Редактирование {product.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Название</Form.Label>
            <Form.Control
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Описание</Form.Label>
            <Form.Control
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Категория</Form.Label>
            <Form.Select
              required
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              {categories.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
              {categories.findIndex((f) => f.id == product.categoryId) == -1 ? (
                <option value={product.categoryId}>Не найдено</option>
              ) : (
                ""
              )}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Подтип</Form.Label>
            <Form.Select
              value={subtypeId}
              onChange={(e) => setSubtypeId(e.target.value)}
            >
              <option value="">Без подтипа</option>
              {subtypes.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Материал</Form.Label>
            <Form.Select
              value={materialId}
              onChange={(e) => setMaterialId(e.target.value)}
            >
              <option value="">Без материала</option>
              {materials.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="use-exact-weights-edit"
              label="Конкретные значения веса (выбор из списка размеров)"
              checked={useExactWeights}
              onChange={(e) => setUseExactWeights(e.target.checked)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Допики</Form.Label>
            <Select
              isMulti
              options={allAdditions.map(a => ({ value: a.id, label: `${a.name} (${a.price}₽)` }))}
              value={linkedAdditions.map(a => ({ value: a.id, label: `${a.name} (${a.price}₽)` }))}
              onChange={handleAdditionsChange}
              placeholder="Привязать допики..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Параметры</Form.Label>
            {!isSimpleProduct && (
              <InputGroup className="mb-2">
                <InputGroup.Text>Размеры</InputGroup.Text>
                <Form.Control value={sizes.length} readOnly />
                <Button onClick={() => setEditSizes(true)}>
                  <MdEdit />
                </Button>
              </InputGroup>
            )}
            {!isSimpleProduct && (
              <InputGroup className="mb-2">
                <InputGroup.Text>Толщина</InputGroup.Text>
                <Form.Control value={thickness.length} readOnly />
                <Button onClick={() => setIsEditThickness(true)}>
                  <MdEdit />
                </Button>
              </InputGroup>
            )}
            <InputGroup className="mb-2">
              <InputGroup.Text>Скидки (шт.)</InputGroup.Text>
              <Form.Control value={priceOnCounts.length} readOnly />
              <Button onClick={() => setIsEditPriceOnCount(true)}>
                <MdEdit />
              </Button>
            </InputGroup>
            <InputGroup className="mb-2">
              <InputGroup.Text>Нанесения</InputGroup.Text>
              <Form.Control value={productCoatings.length} readOnly />
              <Button onClick={() => setIsEditCoatings(true)}>
                <MdEdit />
              </Button>
            </InputGroup>
          </Form.Group>
          <InputGroup className="mb-2">
            <InputGroup.Text>Связи с отправками</InputGroup.Text>
            <Form.Control value="?" readOnly />
            <Button onClick={() => setIsEditMappings(true)}>
              <MdEdit />
            </Button>
          </InputGroup>
          {!isSimpleProduct && (
            <Stack>
              <Form.Group className="mb-3">
                <Form.Label>Максимальный размер</Form.Label>
                <Form.Control
                  required
                  type="number"
                  value={maxSize}
                  onChange={(e) => setMaxSize(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Минимальный размер</Form.Label>
                <Form.Control
                  required
                  type="number"
                  value={minSize}
                  onChange={(e) => setMinSize(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Стандартная толщина</Form.Label>
                <Form.Control
                  required
                  type="number"
                  value={standardThickness}
                  onChange={(e) => setStandardThickness(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Стандартный размер</Form.Label>
                <Form.Control
                  required
                  type="number"
                  value={standardSize}
                  onChange={(e) => setStandardSize(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                />
              </Form.Group>
            </Stack>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Стандартный вес</Form.Label>
            <Form.Control
              required
              type="number"
              value={standardWeight}
              onChange={(e) => setStandardWeight(e.target.value)}
              onWheel={(e) => e.target.blur()}
            />
          </Form.Group>

          {isSimpleProduct && (
            <Form.Group className="mb-3">
              <Form.Label>Цена</Form.Label>
              <Form.Control
                required
                type="number"
                value={standardPrice}
                onChange={(e) => setStandardPrice(e.target.value)}
                onWheel={(e) => e.target.blur()}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Отмена
          </Button>
          <Button variant="danger" onClick={onDelete}>
            Удалить
          </Button>
          <Button variant="success" type="submit">
            Сохранить
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function CoatingSelectionModal({
  show,
  onHide,
  jwt,
  productId,
  productCoatings,
  setProductCoatings,
}) {
  const [allCoatings, setAllCoatings] = useState([]);

  useEffect(() => {
    async function loadCoatings() {
      const response = await HttpNoData(
        "/api/v1/calculateOrderCost/coatings",
        "GET",
        jwt,
      );
      setAllCoatings(response.data ?? []);
    }
    loadCoatings();
  }, [jwt]);

  const addCoating = async (coatingId) => {
    const response = await HttpNoData(
      `/api/v1/calculateOrderCost/products/${productId}/coatings/${coatingId}`,
      "POST",
      jwt,
    );
    if (response.statusSuccessful) {
      const coating = allCoatings.find((c) => c.id === coatingId);
      if (coating) {
        setProductCoatings([...productCoatings, coating]);
      }
    } else {
      alert("Не удалось добавить нанесение");
    }
  };

  const removeCoating = async (coatingId) => {
    const response = await HttpNoData(
      `/api/v1/calculateOrderCost/products/${productId}/coatings/${coatingId}`,
      "DELETE",
      jwt,
    );
    if (response.statusSuccessful) {
      setProductCoatings(productCoatings.filter((c) => c.id !== coatingId));
    } else {
      alert("Не удалось удалить нанесение");
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Нанесения продукта</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-6">
            <h6>Доступные нанесения</h6>
            <ListGroup>
              {allCoatings
                .filter(
                  (coating) =>
                    !productCoatings.find((pc) => pc.id === coating.id),
                )
                .map((coating) => (
                  <ListGroup.Item key={coating.id}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{coating.name}</strong>
                      </div>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => addCoating(coating.id)}
                      >
                        <HiPlus />
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))}
            </ListGroup>
          </div>
          <div className="col-md-6">
            <h6>Выбранные нанесения</h6>
            <ListGroup>
              {productCoatings.map((coating) => (
                <ListGroup.Item key={coating.id}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{coating.name}</strong>
                    </div>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeCoating(coating.id)}
                    >
                      ×
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Назад
        </Button>
      </Modal.Footer>
    </Modal>
  );
}